import type { OrgEventChecklistItemInstance } from 'hooks/orgEvents/orgEventHooks';
import type {
  FacilityDemoFacility,
  FacilityDemoPack,
} from './facilityDemoPack';

export type FacilityAssetCoaProgress = {
  complete: number;
  total: number;
  percent: number;
};

type StoredNotifySent = Record<string, Record<string, string>>;

function readNotifyStore(storageKey: string): StoredNotifySent {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    return JSON.parse(raw) as StoredNotifySent;
  } catch {
    return {};
  }
}

function writeNotifyStore(storageKey: string, data: StoredNotifySent): void {
  localStorage.setItem(storageKey, JSON.stringify(data));
}

export function facilityAssetChecklistKey(
  pack: FacilityDemoPack,
  resource: {
    resource_set_id?: string | null;
    notes?: string | null;
  }
): string {
  return (
    pack.facilityIdFromResourceNotes(resource.notes) ??
    (resource.resource_set_id ? String(resource.resource_set_id) : 'unknown')
  );
}

export function facilityAssetOrgActionName(
  label: string,
  assetLabel: string
): string {
  return `${label} — ${assetLabel}`;
}

export function facilityAssetLabel(
  pack: FacilityDemoPack,
  facility: FacilityDemoFacility
): string {
  return pack.formatFacilityLabel(facility);
}

function notifyItemFor(pack: FacilityDemoPack) {
  return pack.responseChecklistItems.find(
    (item) => item.kind === 'send_notification'
  );
}

export function facilityAssetNotifyItemId(
  pack: FacilityDemoPack,
  assetKey: string
): string {
  return `${notifyItemFor(pack)?.id ?? `${pack.kind}-checklist-accountability`}:${assetKey}`;
}

export function readFacilityAssetNotifySent(
  pack: FacilityDemoPack,
  assetKey: string,
  itemId: string
): string | undefined {
  const sent = readNotifyStore(pack.assetCoaNotifyStorageKey)[assetKey]?.[
    itemId
  ];
  return sent || undefined;
}

export function writeFacilityAssetNotifySent(
  pack: FacilityDemoPack,
  assetKey: string,
  itemId: string,
  notificationId: string
): void {
  const store = readNotifyStore(pack.assetCoaNotifyStorageKey);
  store[assetKey] = { ...store[assetKey], [itemId]: notificationId };
  writeNotifyStore(pack.assetCoaNotifyStorageKey, store);
}

export function buildFacilityAssetNotifyItem(
  pack: FacilityDemoPack,
  assetKey: string,
  assetLabel: string
): Extract<OrgEventChecklistItemInstance, { kind: 'send_notification' }> {
  const item = notifyItemFor(pack);
  const itemId = facilityAssetNotifyItemId(pack, assetKey);
  const notify = item?.kind === 'send_notification' ? item.notify : undefined;
  return {
    kind: 'send_notification',
    id: itemId,
    label: item?.label ?? 'Send accountability check notification',
    notify: notify
      ? { ...notify, ...pack.assetNotifyTemplates(assetLabel) }
      : undefined,
    sentNotificationId: readFacilityAssetNotifySent(pack, assetKey, itemId),
  };
}

export function facilityAssetCoaProgress({
  actionStatuses,
  notifySent,
}: {
  actionStatuses: Array<string | null | undefined>;
  notifySent: boolean;
}): FacilityAssetCoaProgress {
  const actionComplete = actionStatuses.filter(
    (status) => status === 'Closed'
  ).length;
  const complete = actionComplete + (notifySent ? 1 : 0);
  const total = actionStatuses.length + 1;
  const percent = total === 0 ? 0 : Math.round((complete / total) * 100);
  return { complete, total, percent };
}
